import useContainerWidth from "@/hooks/useContainerWidth";
import useDebounce from "@/hooks/useDebounce";
import type { Estimation } from "@/interfaces/estimation.interface";
import { estimationSelector } from "@/redux/selectors/estimation.selector";
import { useSelector } from "@/redux/store";
import { getStorageFileUrl } from "@/util/get-storage-file-url";
import { useCallback, useEffect, useRef, useState } from "react";
import { Document, Thumbnail } from "react-pdf";
import PDFCustomViewer from "../pdf-preview/PDFCustomViewer";
import { Download, FileText } from "lucide-react";
import { EmptyTabState } from "./EmptyTabState";

type SheetsProps = Pick<Estimation, "files">;

export const Sheets = ({ files }: SheetsProps) => {
  const currentRoom = useSelector(estimationSelector.currentRoom);

  const [selectedFilename, setSelectedFilename] = useState<string>();
  const [selectedPdfUrl, setSelectedPdfUrl] = useState<string | null>(null);
  const [viewerTargetPage, setViewerTargetPage] = useState<number>(1);

  const containerRef = useRef<HTMLDivElement>(null);
  const gridContainerWidth = useContainerWidth(containerRef);

  const debouncedSetViewerTargetPage = useDebounce(setViewerTargetPage, 400);

  useEffect(() => {
    let selectedPdfUrl = null;
    let viewerTargetPage = 1;

    if (files.length === 1) {
      selectedPdfUrl = getStorageFileUrl(files[0].remotePath);
      setSelectedFilename(files[0].originalname);
      if (currentRoom) {
        viewerTargetPage = currentRoom.pageIndex ?? 1;
      }
    }
    if (files.length > 0 && currentRoom) {
      if (currentRoom.fileName?.length) {
        const fileName = files.find((file) => file.originalname.includes(currentRoom.fileName ?? ""));
        if (fileName) {
          selectedPdfUrl = getStorageFileUrl(fileName.remotePath);
          viewerTargetPage = currentRoom.pageIndex ?? 1;
          setSelectedFilename(fileName.originalname);
        }
      }
    }
    setSelectedPdfUrl(selectedPdfUrl);
    setViewerTargetPage(viewerTargetPage);
  }, [files, currentRoom]);

  const onDocumentLoadSuccessHandler = useCallback(() => {
    debouncedSetViewerTargetPage(currentRoom?.pageIndex ?? 1);
  }, [currentRoom, debouncedSetViewerTargetPage]);

  if (files.length === 0) {
    return <EmptyTabState icon={<FileText className="h-6 w-6 text-muted-foreground" />} title="No documents" description="No documents have been uploaded for this estimate." />;
  }

  const handleDownload = async () => {
    if (!selectedPdfUrl) return;
    const response = await fetch(selectedPdfUrl, { method: "GET" });
    const blob = await response.blob();
    const blobUrl = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = blobUrl;
    link.setAttribute("download", selectedFilename ?? "document.pdf");
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(blobUrl);
  };

  return (
    <div className="flex h-full w-full flex-col" ref={containerRef}>
      {selectedPdfUrl && (
        <div className="flex justify-end px-4 py-2">
          <Download
            onClick={handleDownload}
            className="h-5 w-5 cursor-pointer text-muted-foreground transition-colors hover:text-foreground"
          />
        </div>
      )}

      {selectedPdfUrl ? (
        <div className="flex h-[90%] w-full flex-1 flex-col items-center justify-center">
          <PDFCustomViewer url={selectedPdfUrl} targetPageNumber={viewerTargetPage} onDocumentLoadSuccess={onDocumentLoadSuccessHandler} />
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-4 overflow-y-auto px-4 py-2">
          {files.map((sheet, index) => {
            const path = getStorageFileUrl(sheet.remotePath);

            if (path.includes("pdf"))
              return (
                <div
                  key={`${path}_${index}`}
                  onClick={() => { setSelectedPdfUrl(path); setSelectedFilename(sheet.originalname); }}
                  className="rounded- flex cursor-pointer items-center justify-center border border-gray-300"
                >
                  <Document file={path} className="flex flex-col items-center">
                    <span className="mt-3 text-center text-xs text-gray-500">{sheet.originalname}</span>
                    <Thumbnail pageNumber={1} width={gridContainerWidth ? gridContainerWidth / 2 - 40 : 180} />
                  </Document>
                </div>
              );

            return (
              <div
                key={sheet.uploadId}
                onClick={() => { setSelectedPdfUrl(path); setSelectedFilename(sheet.originalname); }}
                className="flex items-center justify-center rounded-sm !bg-[#000]"
              >
                <img
                  loading="lazy"
                  className="h-[200px] w-[200px] cursor-pointer rounded-sm object-contain"
                  src={getStorageFileUrl(sheet.remotePath)}
                />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
